// Shared between the card and the details modal so a given payment status
// always looks the same everywhere. Real Order.paymentStatus values are
// lowercase (server/src/orders/order.entity.ts) — 'paid' | 'approved' |
// 'pending' | 'rejected' | 'cancelled' | 'failed' | null — so this carries
// a display label alongside the badge color, not just a class. The ₹
// prefix marks this specifically as a PAYMENT status (vs. the Delivery/QR
// badges next to it, which stay plain text) at a glance.
export const PAYMENT_STATUS_META = {
  paid: { label: "₹ Paid", class: "bg-brand-50 text-brand-700" },
  approved: { label: "₹ Approved", class: "bg-brand-50 text-brand-700" },
  pending: { label: "₹ Pending", class: "bg-amber-50 text-amber-700" },
  rejected: { label: "₹ Rejected", class: "bg-red-50 text-red-600" },
  cancelled: { label: "₹ Cancelled", class: "bg-red-50 text-red-600" },
  failed: { label: "₹ Failed", class: "bg-red-50 text-red-600" },
};

// Dropit's own raw ride status — deliberately distinct from the order's own
// status pill (see order.status), since the two can legitimately disagree
// (e.g. seller cancelled only the Dropit ride). Shared by the Live Orders
// card and the Order Details modal so a given Dropit status always looks
// the same everywhere.
export const DROPIT_STATUS_META = {
  Pending: { label: "Pending", class: "bg-amber-100 text-amber-700" },
  Accepted: { label: "Accepted", class: "bg-blue-100 text-blue-700" },
  ArrivedAtPickup: { label: "Arrived at Pickup", class: "bg-blue-100 text-blue-700" },
  InProgress: { label: "In Progress", class: "bg-indigo-100 text-indigo-700" },
  Completed: { label: "Completed", class: "bg-green-100 text-green-700" },
  Cancelled: { label: "Cancelled", class: "bg-red-100 text-red-700" },
  Aborted: { label: "Aborted", class: "bg-red-100 text-red-700" },
};
export const DROPIT_TERMINAL_STATUSES = ["Completed", "Cancelled", "Aborted"];

// The two order-status KEYS (not labels — see live-order-status.entity.ts,
// key is immutable even if a seller renames the status) that are terminal
// side-states rather than a forward pipeline step: only ever reached via an
// explicit Cancel/Return action, never a "next status" advance, and never a
// valid target to cancel FROM again. This is a fixed pair of identifiers,
// not a list of "all order statuses" — it stays correct however many
// statuses a seller has configured, or what they're labelled/colored/ordered
// as, so it doesn't need to come from the DB the way status labels/order/
// colors/dropdown options do. Mirrors ORDER_TERMINAL_STATUS_KEYS in
// server/src/orders/order-status-rules.util.ts — keep both in sync.
export const ORDER_TERMINAL_STATUS_KEYS = ["Cancelled", "Returned"];

// Distinct colors per fulfillment type so it's identifiable at a glance,
// separate from each seller-configured status's own color (statusMeta).
// Order.fulfillmentMethod is normalized to Title Case at write time.
export const FULFILLMENT_BADGE_CLASS = {
  Delivery: "bg-blue-50 text-blue-700",
  Pickup: "bg-purple-50 text-purple-700",
};

/**
 * "5 min ago" / "1 hour ago" while recent, falling back to a full date +
 * time once it's old enough that "N hours ago" stops being useful — used
 * for both the card's age indicator and the details modal header. Returns
 * an i18n key + count (for the relative forms) OR a plain already-formatted
 * string (for the date fallback, which isn't translatable text), so the
 * caller just does `display.key ? t(display.key, { count: display.count })
 * : display.text`.
 */
export function getOrderTimeDisplay(dateInput) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);

  if (minutes < 1) return { key: "liveOrders.time.justNow" };
  if (minutes < 60) return { key: "liveOrders.time.minutesAgo", count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: "liveOrders.time.hoursAgo", count: hours };

  return {
    key: null,
    text: `${date.toLocaleDateString([], { day: "2-digit", month: "short" })}, ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`,
  };
}

/**
 * Payment breakdown for the modal's Payment Summary section, built entirely
 * from real, stored Order fields — no fabricated tax line. GST/CGST/SGST is
 * never persisted per-order (see order-invoice.service.ts): it's computed
 * only at receipt-PDF time from the seller's Settings → Receipt
 * Customization percentages, which isn't a fact about the order itself, so
 * it has no place here.
 */
/**
 * A "Free delivery within X km" Delivery Option rule (see
 * server/src/orders/shipping-fee.util.ts) waives the fee for the customer
 * but the seller still owes it to the courier — order.shippingFee always
 * holds that real amount, order.freeDelivery says whether it was waived.
 * Callers render `fee` struck-through + "Free" when `waived`, plain "Free"
 * when fee is 0, else the plain amount.
 */
export function deliveryFeeParts(order) {
  const fee = Number(order.shippingFee) || 0;
  const waived = order.freeDelivery === true && fee > 0;
  return { fee, waived };
}

// Independent-mode "Use Main Product Price" products on the web storefront
// fan one Add into several order items sharing a productId — one per
// selected option — but only the first is ever really priced (see
// ChatflowEngineService.validateWebOrderItems); the rest are stored with
// price 0 and zeroPriced: true purely to record which extra option was
// picked. Displaying them as their own rows would show a confusing "₹0.00"
// line and inflate the item count, so every list that renders order.items
// groups them back into their priced sibling's row first — one row per
// product, with every selected option listed together, matching how the
// storefront's own cart already groups them (see CheckoutPage.jsx).
// Orders placed via WhatsApp's native catalog (not the web storefront)
// never have zeroPriced items, so this is a no-op for them — every item
// just becomes its own single-option group, unchanged from before.
export function groupOrderItems(items) {
  const itemLabel = (item) =>
    item.variantLabel || (item.variants && Object.keys(item.variants).length > 0 ? Object.values(item.variants).join(", ") : null);
  const merged = [];
  (items || []).forEach((item) => {
    const label = itemLabel(item);
    const key = item.productId || item.retailerId;
    const leader = item.zeroPriced ? [...merged].reverse().find((m) => (m.item.productId || m.item.retailerId) === key) : null;
    if (leader) {
      if (label) leader.variantLabels.push(label);
    } else {
      merged.push({ item, variantLabels: label ? [label] : [] });
    }
  });
  return merged;
}

export function derivePaymentBreakdown(order) {
  const grandTotal = Number(order.totalAmount) || 0;
  // shippingFee always holds the real fee amount (for the seller to see what
  // they owe), but a freeDelivery order never had it folded into grandTotal
  // in the first place (see OrdersService.createFromSession) — only
  // subtract it here when it was actually charged to the customer.
  const deliveryCharges = Number(order.shippingFee) || 0;
  const freeDelivery = order.freeDelivery === true;
  const subtotal = Math.round((grandTotal - (freeDelivery ? 0 : deliveryCharges)) * 100) / 100;
  return { subtotal, deliveryCharges, grandTotal, freeDelivery };
}

/**
 * GST breakdown for the Live Orders details modal/cards, the Order Details
 * page, and the Orders Listing table's Amount column — the underlying
 * CGST/SGST math is calculated on the product subtotal ONLY, never the
 * delivery fee, exactly matching every Interactive Message / WhatsApp
 * order-summary flow (see chatflow-engine.service.ts's
 * `computeGst(subtotal, gstPercent, gstInclusive, subtotal + shippingFee)`
 * calls — taxBase is subtotal, totalBase is subtotal+shipping, delivery is
 * only folded into the FINAL total, never into what the % is multiplied
 * against). This previously multiplied against grandTotal (subtotal +
 * shipping) by mistake, overcharging GST on the delivery fee — the invoice
 * PDF (OrderInvoiceService.buildInvoicePdf) is a separate, deliberately
 * different calculation and is unaffected by this fix.
 *
 * Combined into a single GST rate/amount for display here (e.g. CGST 2.5% +
 * SGST 2.5% shown as one "GST (5%)" line) — the invoice PDF is the place a
 * legal CGST/SGST split needs to be itemized separately, not this
 * at-a-glance seller view. Returns null when neither tax is configured/shown
 * (covers the "GST is 0/empty" special case too — gstAmount is 0 either
 * way), so the modal can skip rendering the section entirely rather than
 * showing a row of zeroes.
 *
 * `receiptSettings.gstInclusive` (Settings → Tax Settings → "Include GST in
 * Product Prices") controls whether GST is added on top of grandTotal
 * (Exclusive, default) or just shown for reference because the seller's
 * prices already include it (Inclusive) — see SettingsService.getGstBreakdown
 * for the equivalent server-side logic.
 */
export function deriveGstBreakdown(order, receiptSettings) {
  const { subtotal, grandTotal } = derivePaymentBreakdown(order);
  const show = receiptSettings?.show || {};
  const cgstPercent = Number(receiptSettings?.cgstPercent) || 0;
  const sgstPercent = Number(receiptSettings?.sgstPercent) || 0;
  const cgstAmount = show.cgst && cgstPercent ? subtotal * (cgstPercent / 100) : 0;
  const sgstAmount = show.sgst && sgstPercent ? subtotal * (sgstPercent / 100) : 0;
  const gstAmount = cgstAmount + sgstAmount;
  if (gstAmount <= 0) return null;

  const gstPercent = (show.cgst ? cgstPercent : 0) + (show.sgst ? sgstPercent : 0);
  const gstInclusive = !!receiptSettings?.gstInclusive;
  const totalWithTax = gstInclusive ? grandTotal : grandTotal + gstAmount;
  return {
    gstPercent,
    gstAmount,
    gstInclusive,
    totalWithTax,
    roundedTotal: Math.round(totalWithTax),
  };
}

/**
 * Timeline steps for the modal's Order Timeline section — always the full
 * status sequence, in a fixed order, each marked from the order's REAL
 * statusHistory rather than from "current status's position in the chain".
 * That distinction matters for a cancelled order: position-based logic can
 * only ever show the entry step then Cancelled, discarding every stage
 * actually reached before cancellation (e.g. an order cancelled while Out
 * for Delivery would wrongly render as if it went straight from Received to
 * Cancelled). History-based logic keeps every genuinely-reached stage
 * marked "completed" instead.
 *
 * Sequence is `forwardChainKeys` (the seller's configured active statuses,
 * in board order) with the two terminal/side-state branches placed last,
 * Cancelled before Returned — regardless of where either happens to sort in
 * the seller's own column config, since forwardChainKeys already excludes
 * Cancelled entirely (it's never a "next status" quick-action target) but
 * doesn't exclude Returned, which would otherwise land ahead of Cancelled
 * whenever both show up in one order's timeline.
 *
 * Each step gets a `state`: "completed" (reached and since moved past),
 * "current" (the order's status right now), "cancelled" (the order's
 * terminal Cancelled step specifically), or "pending" (not yet reached).
 * `done` is kept alongside as `state !== "pending"` for callers that only
 * need the boolean (e.g. the connecting-line color between steps).
 */
// "12 Aug, 03:45 PM" — date alongside the time, on one line. Shared by every
// status-timeline rendering (the seller-facing OrderStatusTimeline and the
// public tracking page's OrderProgressBar) so a given step's timestamp
// always looks the same everywhere.
export function formatStepDateTime(date) {
  const datePart = date.toLocaleDateString([], { day: "2-digit", month: "short" });
  const timePart = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${datePart}, ${timePart}`;
}

export function buildOrderTimeline(order, forwardChainKeys = []) {
  const history = order.statusHistory || [];
  const timestampFor = (status) => {
    const entry = history.find((h) => h.status === status);
    return entry ? new Date(entry.changedAt) : null;
  };

  // Reached = actually recorded in statusHistory, or the order's status
  // right now (covers the rare case where the first history entry doesn't
  // exactly match forwardChainKeys[0]).
  const reached = new Set(history.map((h) => h.status));
  reached.add(order.status);

  const sequence = [...forwardChainKeys.filter((s) => s !== "Returned"), "Cancelled", "Returned"];

  return sequence.map((status) => {
    let state;
    if (status === order.status) state = status === "Cancelled" ? "cancelled" : "current";
    else if (reached.has(status)) state = "completed";
    else state = "pending";
    // Any step actually being shown (i.e. not pending) always gets a real
    // timestamp — statusHistory should have one, but order.updatedAt is a
    // reasonable stand-in for the rare row that predates statusHistory
    // tracking, so a reached step never falls through to a "Pending" label.
    const fallbackTimestamp = state !== "pending" ? new Date(order.updatedAt) : null;
    return { status, timestamp: timestampFor(status) || fallbackTimestamp, state, done: state !== "pending" };
  });
}
